import { useCallback, useEffect, useMemo } from 'react';
import { getErrorMessage } from '../../../shared/errorMessage';
import { presentUnexpectedFailure } from '../../../shared/sentry/presentUnexpectedFailure';
import { showError, showInfo } from '../../../shared/ui/appToast';
import { lookupProductByBarcode } from '../../../catalog/infra-api';
import { isInventoriableProduct } from '../../../inventory/stock/public-api';
import { createBarcodeScanDedupe, looksLikeBarcodeHardwareSubmit } from '../../../shared/product-search/productBarcodeInput';
import { useBranchBarcodeInputModeForCurrentBranch } from '../../../shell/session/hooks/useBranchBarcodeInputMode';

interface UseInventoryBarcodeHandlersParams {
  searchQuery: string;
  productQuery: string;
  bulkSearch: string;
  transferPickerSearch: string;
  setSearchQuery: (q: string) => void;
  setProductQuery: (q: string) => void;
  setLookupFeedback: (f: string | null) => void;
  setEntryScanFeedback: (f: string | null) => void;
  setEntrySuggestion: (s: any) => void;
  setSelectedProduct: (p: any) => void;
  setEntryMinQuantity: (q: string) => void;
  setEntrySupplierId: (id: string | null) => void;
  setTransferPickerSearch: (s: string) => void;
  setSearchScannerVisible: (v: boolean) => void;
  setEntryScannerVisible: (v: boolean) => void;
  setBulkScannerVisible: (v: boolean) => void;
  setTransferPickerScannerVisible: (v: boolean) => void;
  clearPinnedStockSearch: () => void;
  stocks: any[];
  handleBulkScan: (code: string) => void;
}

export const useInventoryBarcodeHandlers = (params: UseInventoryBarcodeHandlersParams) => {
  const {
    searchQuery,
    productQuery,
    bulkSearch,
    transferPickerSearch,
    setSearchQuery,
    setProductQuery,
    setLookupFeedback,
    setEntryScanFeedback,
    setEntrySuggestion,
    setSelectedProduct,
    setEntryMinQuantity,
    setEntrySupplierId,
    setTransferPickerSearch,
    setSearchScannerVisible,
    setEntryScannerVisible,
    setBulkScannerVisible,
    setTransferPickerScannerVisible,
    clearPinnedStockSearch,
    stocks,
    handleBulkScan,
  } = params;

  const { allowCamera: barcodeAllowCamera, allowHardwareWedge: barcodeAllowHardware } =
    useBranchBarcodeInputModeForCurrentBranch();

  const listBarcodeDedupe = useMemo(() => createBarcodeScanDedupe(), []);
  const entryBarcodeDedupe = useMemo(() => createBarcodeScanDedupe(), []);
  const bulkBarcodeDedupe = useMemo(() => createBarcodeScanDedupe(), []);
  const transferPickerBarcodeDedupe = useMemo(() => createBarcodeScanDedupe(), []);

  useEffect(() => {
    if (!barcodeAllowCamera) {
      setSearchScannerVisible(false);
      setEntryScannerVisible(false);
      setBulkScannerVisible(false);
      setTransferPickerScannerVisible(false);
    }
  }, [barcodeAllowCamera, setSearchScannerVisible, setEntryScannerVisible, setBulkScannerVisible, setTransferPickerScannerVisible]);

  const handleInventorySearchScan = useCallback(
    async (rawCode: string) => {
      setSearchScannerVisible(false);
      clearPinnedStockSearch();

      try {
        const result = await lookupProductByBarcode({ code: rawCode, allowRemoteLookup: true });
        setSearchQuery(result.barcode);

        if (result.kind !== 'local_hit') {
          setLookupFeedback('No encontramos ese producto dentro del stock local de la sucursal.');
          return;
        }
        if (!isInventoriableProduct(result.product)) {
          setLookupFeedback('Ese código corresponde a un servicio. Los servicios no llevan stock.');
          return;
        }

        setLookupFeedback(`Producto ubicado: ${result.product.name}.`);
        // FIND: conserva el código para filtrar; ProminentSearch selecciona todo para el próximo HID.

        const stockRow = stocks.find((row) => row.productId === result.product.id);
        if (!stockRow) {
          showInfo('Sin stock cargado', 'Ese producto está en el catálogo, pero todavía no tiene stock acá.');
        }
      } catch (error: unknown) {
        presentUnexpectedFailure({ module: 'inventory', op: 'barcode_scan', error, userMessage: 'No pudimos completar esto.' });
      }
    },
    [
      setSearchScannerVisible,
      clearPinnedStockSearch,
      setSearchQuery,
      setLookupFeedback,
      stocks,
    ]
  );

  const handleEntryScan = useCallback(
    async (rawCode: string) => {
      setEntryScannerVisible(false);
      setEntryScanFeedback(null);

      try {
        const result = await lookupProductByBarcode({ code: rawCode });
        setProductQuery(result.barcode);

        if (result.kind === 'local_hit') {
          if (!isInventoriableProduct(result.product)) {
            setSelectedProduct(null);
            setEntrySuggestion(null);
            setEntryScanFeedback('Ese código corresponde a un servicio. Los servicios no llevan stock.');
            return;
          }
          setEntrySuggestion(null);
          setSelectedProduct(result.product);
          const existingStock = stocks.find((row) => row.productId === result.product.id);
          setEntryMinQuantity(existingStock ? String(existingStock.minQuantity) : '0');
          setEntrySupplierId(result.product.preferredSupplierId ?? null);
          // Pick: limpiar el campo — el producto queda en la card; "Cambiar" reabre búsqueda limpia.
          setProductQuery('');
          setEntryScanFeedback(`Seleccionado: ${result.product.name}`);
          return;
        }

        setSelectedProduct(null);

        if (result.kind === 'remote_hit') {
          setEntrySuggestion(result);
          setEntryScanFeedback('Encontramos una sugerencia online. Cargala primero en Catálogo.');
          return;
        }

        setEntrySuggestion(null);
        if (result.kind === 'offline_miss') {
          showError('Sin conexión', 'Ese código no está acá y no hay internet para buscar una sugerencia.');
        } else {
          showError('Producto no encontrado', 'Ese código no está en el catálogo. Crealo primero en Catálogo.');
        }
      } catch (error: unknown) {
        presentUnexpectedFailure({ module: 'inventory', op: 'barcode_scan', error, userMessage: 'No pudimos completar esto.' });
      }
    },
    [
      setEntryScannerVisible,
      setEntryScanFeedback,
      setProductQuery,
      setSelectedProduct,
      setEntrySuggestion,
      setEntryMinQuantity,
      setEntrySupplierId,
      stocks,
    ]
  );

  const handleTransferPickerScan = useCallback(
    async (rawCode: string) => {
      setTransferPickerScannerVisible(false);
      try {
        const result = await lookupProductByBarcode({ code: rawCode, allowRemoteLookup: true });
        setTransferPickerSearch(result.barcode);
        if (result.kind === 'local_hit' && !isInventoriableProduct(result.product)) {
          setTransferPickerSearch('');
          showInfo('Servicio sin stock', 'Los servicios no se pueden transferir.');
        }
      } catch {
        setTransferPickerSearch(rawCode.trim());
      }
    },
    [setTransferPickerSearch]
  );

  const handleMainSearchHardwareSubmit = useCallback(() => {
    if (!barcodeAllowHardware) return;
    const q = searchQuery.trim();
    if (!looksLikeBarcodeHardwareSubmit(q)) return;
    if (!listBarcodeDedupe(q)) return;
    void handleInventorySearchScan(q);
  }, [barcodeAllowHardware, searchQuery, listBarcodeDedupe, handleInventorySearchScan]);

  const handleEntryProductQueryHardwareSubmit = useCallback(() => {
    if (!barcodeAllowHardware) return;
    const q = productQuery.trim();
    if (!looksLikeBarcodeHardwareSubmit(q)) return;
    if (!entryBarcodeDedupe(q)) return;
    void handleEntryScan(q);
  }, [barcodeAllowHardware, productQuery, entryBarcodeDedupe, handleEntryScan]);

  const handleBulkSearchHardwareSubmit = useCallback(() => {
    if (!barcodeAllowHardware) return;
    const q = bulkSearch.trim();
    if (!looksLikeBarcodeHardwareSubmit(q)) return;
    if (!bulkBarcodeDedupe(q)) return;
    void handleBulkScan(q);
  }, [barcodeAllowHardware, bulkSearch, bulkBarcodeDedupe, handleBulkScan]);

  const handleTransferPickerSearchHardwareSubmit = useCallback(() => {
    if (!barcodeAllowHardware) return;
    const q = transferPickerSearch.trim();
    if (!looksLikeBarcodeHardwareSubmit(q)) return;
    if (!transferPickerBarcodeDedupe(q)) return;
    void (async () => {
      try {
        const result = await lookupProductByBarcode({ code: q, allowRemoteLookup: true });
        setTransferPickerSearch(result.barcode);
        if (result.kind === 'local_hit' && !isInventoriableProduct(result.product)) {
          setTransferPickerSearch('');
          showInfo('Servicio sin stock', 'Los servicios no se pueden transferir.');
        }
      } catch {
        setTransferPickerSearch(q);
      }
    })();
  }, [barcodeAllowHardware, transferPickerSearch, transferPickerBarcodeDedupe, setTransferPickerSearch]);

  return {
    barcodeAllowCamera,
    barcodeAllowHardware,
    handleInventorySearchScan,
    handleEntryScan,
    handleTransferPickerScan,
    handleMainSearchHardwareSubmit,
    handleEntryProductQueryHardwareSubmit,
    handleBulkSearchHardwareSubmit,
    handleTransferPickerSearchHardwareSubmit,
    autoFocusInventorySearchOnWeb: false,
  };
};
